import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, Play, Download, Calendar, Clock, Users, TrendingUp,
  BarChart3, PieChart, Table, Plus, Filter, Search, Star, 
  Bookmark, Share2, Trash2, Settings, RefreshCw, CheckCircle,
  DollarSign, Activity, Heart, Briefcase, Clipboard
} from "lucide-react";

const CATEGORY_ICONS = {
  operations: Briefcase,
  quality: CheckCircle,
  financial: DollarSign,
  clinical: Activity,
  hr: Users
};

const CATEGORY_COLORS = {
  operations: "bg-blue-100 text-blue-700",
  quality: "bg-green-100 text-green-700",
  financial: "bg-emerald-100 text-emerald-700",
  clinical: "bg-purple-100 text-purple-700",
  hr: "bg-orange-100 text-orange-700"
};

export default function ReportBuilder() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [customReports, setCustomReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState("templates");
  const [categoryFilter, setCategoryFilter] = useState("all");
  
  const userId = user?.id || "demo_user";

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [templatesRes, customRes] = await Promise.all([
        api.get("/phase25/reports/templates"),
        api.get(`/phase25/reports/custom?user_id=${userId}`)
      ]);
      setTemplates(templatesRes.data.templates || []);
      setCustomReports(customRes.data.reports || []);
    } catch (error) {
      console.error("Failed to load reports:", error);
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const runReport = async (reportId) => {
    setRunning(true);
    setReportData(null);
    try {
      const res = await api.post(`/phase25/reports/${reportId}/run`, { filters: {} });
      setReportData(res.data);
      toast.success("Report generated successfully");
    } catch (error) {
      console.error("Failed to run report:", error);
      toast.error("Failed to generate report");
    } finally {
      setRunning(false);
    }
  };

  const exportReport = async (reportId, format) => {
    try {
      const res = await api.get(`/phase25/reports/${reportId}/export?format=${format}`);
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  const filteredTemplates = categoryFilter === "all" 
    ? templates 
    : templates.filter(t => t.category === categoryFilter);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="report-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="report-builder">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <FileText className="h-6 w-6" />
                Report Builder
              </h1>
              <p className="text-muted-foreground">Generate insights from your hospice data</p>
            </div>
            <div className="flex gap-2">
              <Dialog>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    New Custom Report
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Create Custom Report</DialogTitle>
                  </DialogHeader>
                  <div className="py-4">
                    <p className="text-muted-foreground text-center py-8">
                      Custom report builder coming soon! Use templates for now.
                    </p>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Report List */}
          <div className="lg:col-span-1 space-y-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="templates" data-testid="templates-tab">
                  <Clipboard className="h-4 w-4 mr-2" />Templates
                </TabsTrigger>
                <TabsTrigger value="custom" data-testid="custom-tab">
                  <Star className="h-4 w-4 mr-2" />My Reports
                </TabsTrigger>
              </TabsList>

              <TabsContent value="templates" className="space-y-4">
                {/* Category Filter */}
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant={categoryFilter === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCategoryFilter("all")}
                  >
                    All
                  </Button>
                  {["operations", "quality", "financial", "clinical", "hr"].map((cat) => {
                    const Icon = CATEGORY_ICONS[cat] || FileText;
                    return (
                      <Button
                        key={cat}
                        variant={categoryFilter === cat ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCategoryFilter(cat)}
                        className="capitalize"
                      >
                        <Icon className="h-3 w-3 mr-1" />
                        {cat}
                      </Button>
                    );
                  })}
                </div>

                {/* Template List */}
                <div className="space-y-2">
                  {filteredTemplates.map((template) => {
                    const Icon = CATEGORY_ICONS[template.category] || FileText;
                    return (
                      <Card
                        key={template.id}
                        className={`cursor-pointer transition-all hover:shadow-md ${
                          selectedReport?.id === template.id ? "ring-2 ring-primary" : ""
                        }`}
                        onClick={() => {
                          setSelectedReport(template);
                          setReportData(null);
                        }}
                        data-testid={`template-${template.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${CATEGORY_COLORS[template.category]}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-sm">{template.name}</h3>
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {template.description}
                              </p>
                              <Badge variant="outline" className="mt-2 text-xs capitalize">
                                {template.category}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="custom" className="space-y-4">
                {customReports.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <Star className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">No custom reports yet</p>
                      <p className="text-sm text-muted-foreground">Create one using the builder</p>
                    </CardContent>
                  </Card>
                ) : (
                  customReports.map((report) => (
                    <Card
                      key={report.id}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        selectedReport?.id === report.id ? "ring-2 ring-primary" : ""
                      }`}
                      onClick={() => {
                        setSelectedReport(report);
                        setReportData(null);
                      }}
                    >
                      <CardContent className="p-4">
                        <h3 className="font-semibold">{report.name}</h3>
                        <p className="text-sm text-muted-foreground">{report.description}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          Created: {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Panel - Report Details & Results */}
          <div className="lg:col-span-2 space-y-4">
            {!selectedReport ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <BarChart3 className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Select a Report</h3>
                  <p className="text-muted-foreground">Choose a template or custom report to generate</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Report Header */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-xl font-bold">{selectedReport.name}</h2>
                        <p className="text-muted-foreground mt-1">{selectedReport.description}</p>
                        <div className="flex gap-2 mt-3">
                          <Badge variant="outline" className="capitalize">
                            {selectedReport.category}
                          </Badge>
                          {selectedReport.visualizations?.map((viz) => (
                            <Badge key={viz} variant="secondary" className="text-xs">
                              {viz.replace("_", " ")}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => exportReport(selectedReport.id, "pdf")}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Export
                        </Button>
                        <Button
                          onClick={() => runReport(selectedReport.id)}
                          disabled={running}
                          data-testid="run-report-btn"
                        >
                          {running ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Running...
                            </>
                          ) : (
                            <>
                              <Play className="h-4 w-4 mr-2" />
                              Run Report
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Report Results */}
                {reportData && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>Report Results</CardTitle>
                          <CardDescription>
                            Generated: {new Date(reportData.generated_at).toLocaleString()}
                          </CardDescription>
                        </div>
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Complete
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Render report data based on type */}
                      {renderReportData(selectedReport.id, reportData.data)}
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function to render report data
function renderReportData(reportId, data) {
  if (!data) return null;

  // Daily Census Report
  if (reportId === "daily_census") {
    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{data.metrics?.total_patients}</p>
              <p className="text-sm text-muted-foreground">Total Patients</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-green-600">+{data.metrics?.admissions_today}</p>
              <p className="text-sm text-muted-foreground">Admissions Today</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-orange-600">{data.metrics?.discharges_today}</p>
              <p className="text-sm text-muted-foreground">Discharges</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{data.metrics?.average_los}</p>
              <p className="text-sm text-muted-foreground">Avg LOS (days)</p>
            </CardContent>
          </Card>
        </div>

        {/* By Location */}
        <div>
          <h4 className="font-semibold mb-3">Census by Location</h4>
          <div className="space-y-2">
            {data.by_location?.map((loc) => (
              <div key={loc.location} className="flex items-center gap-2">
                <span className="w-32 text-sm">{loc.location}</span>
                <Progress value={(loc.count / data.metrics?.total_patients) * 100} className="flex-1 h-2" />
                <span className="w-12 text-sm text-right">{loc.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Visit Productivity Report
  if (reportId === "visit_productivity") {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{data.summary?.visits_completed}</p>
              <p className="text-sm text-muted-foreground">Visits Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-green-600">{data.summary?.completion_rate}%</p>
              <p className="text-sm text-muted-foreground">Completion Rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{data.summary?.avg_duration_minutes}</p>
              <p className="text-sm text-muted-foreground">Avg Duration (min)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-orange-600">{data.summary?.total_drive_miles}</p>
              <p className="text-sm text-muted-foreground">Total Miles</p>
            </CardContent>
          </Card>
        </div>

        <div>
          <h4 className="font-semibold mb-3">Staff Performance</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Staff Member</th>
                  <th className="text-center py-2">Planned</th>
                  <th className="text-center py-2">Completed</th>
                  <th className="text-center py-2">Rate</th>
                </tr>
              </thead>
              <tbody>
                {data.by_staff?.map((staff) => (
                  <tr key={staff.name} className="border-b">
                    <td className="py-2">{staff.name}</td>
                    <td className="text-center py-2">{staff.planned}</td>
                    <td className="text-center py-2">{staff.completed}</td>
                    <td className="text-center py-2">
                      <Badge className={staff.rate >= 95 ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                        {staff.rate}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Financial Summary
  if (reportId === "financial_summary") {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-green-600">
                ${(data.summary?.total_revenue / 1000).toFixed(0)}K
              </p>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{data.summary?.claims_paid}</p>
              <p className="text-sm text-muted-foreground">Claims Paid</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-red-600">{data.summary?.denial_rate}%</p>
              <p className="text-sm text-muted-foreground">Denial Rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-orange-600">{data.summary?.average_days_in_ar}</p>
              <p className="text-sm text-muted-foreground">Days in A/R</p>
            </CardContent>
          </Card>
        </div>

        <div>
          <h4 className="font-semibold mb-3">Revenue by Payer</h4>
          <div className="space-y-2">
            {data.by_payer?.map((payer) => (
              <div key={payer.payer} className="flex items-center gap-2">
                <span className="w-32 text-sm">{payer.payer}</span>
                <Progress value={payer.percent} className="flex-1 h-2" />
                <span className="w-20 text-sm text-right">${(payer.revenue / 1000).toFixed(0)}K</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Quality Measures
  if (reportId === "quality_measures") {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-green-600">{data.cahps_scores?.overall_rating}%</p>
              <p className="text-sm text-muted-foreground">Overall Rating</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{data.cahps_scores?.willingness_to_recommend}%</p>
              <p className="text-sm text-muted-foreground">Would Recommend</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{data.cahps_scores?.communication}%</p>
              <p className="text-sm text-muted-foreground">Communication</p>
            </CardContent>
          </Card>
        </div>

        <div>
          <h4 className="font-semibold mb-3">Quality Indicators vs Benchmark</h4>
          <div className="space-y-3">
            {data.quality_indicators?.map((qi) => (
              <div key={qi.measure} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span>{qi.measure}</span>
                  <span className={qi.score >= qi.benchmark ? "text-green-600" : "text-red-600"}>
                    {qi.score}% (benchmark: {qi.benchmark}%)
                  </span>
                </div>
                <Progress 
                  value={qi.score} 
                  className={`h-2 ${qi.score >= qi.benchmark ? "bg-green-100" : "bg-red-100"}`} 
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Default: show raw data
  return (
    <pre className="text-xs bg-slate-100 p-4 rounded-lg overflow-auto max-h-96">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}
